import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { OpCollapse } from 'components/customAntd/DLS/OpCollapse/OpCollapse';
import dayjs from 'dayjs';
import { Select, DatePicker, Button, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { describeLocation, describeLocationWorkflow, fetchLocations } from 'store/slices/locationsSlice';
import { RootState, AppDispatch } from 'store/store';
import type { DatePickerProps, RangePickerProps } from 'antd/es/date-picker';
import { DATE_TIME_FORMAT } from 'constants/dates';
import { OpCol } from './DLS/OpCol/OpCol';
import { OpRow } from './DLS/OpRow/OpRow';
import enUS from 'antd/es/date-picker/locale/en_US';
// import koKR from 'antd/es/date-picker/locale/ko_KR';
import esES from 'antd/es/date-picker/locale/es_ES';
import deDE from 'antd/es/date-picker/locale/de_DE';
import itIT from 'antd/es/date-picker/locale/it_IT';
import frFR from 'antd/es/date-picker/locale/fr_FR';
import ptPT from 'antd/es/date-picker/locale/pt_PT';

interface DateRangeLocationFilterProps {
    onDateRangeLocationFilter: (locationId: number, startDate: string, endDate: string) => void;
    initialStartDate: string;
    initialEndDate: string;
}

const { RangePicker } = DatePicker;

const DateRangeLocationFilter: React.FC<DateRangeLocationFilterProps> = ({
    onDateRangeLocationFilter,
    initialStartDate,
    initialEndDate
}) => {
    const { t } = useTranslation();
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const { locations, fetchLocationsLoading } = useSelector((state: RootState) => state.locations);

    const { i18n } = useTranslation();
    const datePickerLocaleMap = {
        en: enUS,
        // ko: koKR,
        es: esES,
        de: deDE,
        it: itIT,
        fr: frFR,
        pt: ptPT,
    };

    const datePickerLocale = datePickerLocaleMap[i18n.language as keyof typeof datePickerLocaleMap] || enUS;

    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs(initialStartDate),
        dayjs(initialEndDate)
    ]);

    useEffect(() => {
        if (orgId && locations.length === 0) {
            dispatch(fetchLocations({ orgId, status: 1 }));
        }
    }, [dispatch, orgId, locations.length]);

    const handleUpdate = () => {
        if (globalLocationId && dateRange && dateRange[0] && dateRange[1]) {
            const formattedStartDate = dateRange[0].startOf('day').format(DATE_TIME_FORMAT);
            const formattedEndDate = dateRange[1].endOf('day').format(DATE_TIME_FORMAT);
            onDateRangeLocationFilter(globalLocationId, formattedStartDate, formattedEndDate);
            notification.success({
                message: t('notification.success'),
                description: t('dateRangeLocationFilter.filterSuccess'),
                placement: 'bottomRight'
            });
        }
    };

    const handleLocationChange = async (locationId: number) => {
        await dispatch(describeLocation({ orgId, locationId, global: true }));
        await dispatch(describeLocationWorkflow({ orgId, locationId, global: true }));
    };

    const disabled7DaysDate: DatePickerProps['disabledDate'] = (current, { from }) => {
        if (from) return Math.abs(current.diff(from, 'days')) >= 7;
        return false;
    };

    const rangePresets: RangePickerProps['presets'] = [
        { label: t('dateRangeLocationFilter.today'), value: [dayjs().startOf('day'), dayjs().endOf('day')] },
        { label: t('dateRangeLocationFilter.tomorrow'), value: [dayjs().add(1, 'day').startOf('day'), dayjs().add(1, 'day').endOf('day')] },
        { label: t('dateRangeLocationFilter.yesterday'), value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')] },
        { label: t('dateRangeLocationFilter.thisWeek'), value: [dayjs().startOf('week'), dayjs().endOf('week')] },
        { label: t('dateRangeLocationFilter.nextWeek'), value: [dayjs().add(1, 'week').startOf('week'), dayjs().add(1, 'week').endOf('week')] },
        { label: t('dateRangeLocationFilter.lastWeek'), value: [dayjs().subtract(1, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')] },
    ];

    return (
        <OpCollapse defaultActiveKey={['1']} items={[{
            key: '1',
            label: t('dateRangeLocationFilter.label'),
            children: (
                <OpRow gutter={[16, 16]} align="middle" style={{ width: '100%' }}>
                    <OpCol xs={24} sm={24} md={4} lg={6}>
                        <Select
                            value={
                                fetchLocationsLoading
                                    ? undefined
                                    : globalLocationId === -1
                                        ? -1
                                        : locations.find(location => location.id === globalLocationId)?.id
                            }
                            style={{ width: '100%' }}
                            options={
                                locations.length > 1
                                    ? [{ label: t('dateRangeLocationFilter.allLocations'), value: -1 }, ...locations.map(location => ({ value: location.id, label: location.name }))]
                                    : locations.map(location => ({ value: location.id, label: location.name }))
                            }
                            onChange={handleLocationChange}
                            loading={fetchLocationsLoading}
                            placeholder={t('dateRangeLocationFilter.selectLocation')}
                        />
                    </OpCol>

                    <OpCol xs={24} sm={24} md={16} lg={14}>
                        <RangePicker
                            locale={datePickerLocale}
                            style={{ width: '100%' }}
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                            presets={rangePresets}
                            disabledDate={disabled7DaysDate}
                        />
                    </OpCol>

                    <OpCol xs={24} sm={24} md={4} lg={4}>
                        <Button type="primary" style={{ width: '100%' }} onClick={handleUpdate}>
                            {t('dateRangeLocationFilter.update')}
                        </Button>
                    </OpCol>
                </OpRow>
            )
        }]} />
    );
};

export default DateRangeLocationFilter;
